import React, { useCallback } from "react";
import { useTranslation } from "react-i18next";

import { ISelectProps, Select } from "@/components/Select";
import { Text } from "@/components/Text";
import { fetchTasksOriginServices, IFetchTasksOriginServicesParams, ITaskOriginService } from "@/data/tasks";
import { codelistFactory, Flex, FloatedList, ICodelistFactoryProps, ReactSelectComponents } from "@/eworkitComponents";
import { IFilterComponentProps, useOnChangeFilterHandler } from "@/infrastructure/Filter";

import { SkillRemove } from "../Forms/Skills/components";

interface ICustomerCodelistProps
  extends ICodelistFactoryProps<ISelectProps, ITaskOriginService, IFetchTasksOriginServicesParams> {}

const CustomerCodelist = codelistFactory<ISelectProps, ITaskOriginService, IFetchTasksOriginServicesParams>(
  fetchTasksOriginServices,
  Select,
);

interface Props
  extends IFilterComponentProps<ITaskOriginService[]>,
    Omit<ICustomerCodelistProps, "name" | "onChange" | "value"> {}

const CustomerFilerComponent: React.FC<Props> = ({ name, onChange, value = [], ...rest }) => {
  const { t } = useTranslation(["common", "session"]);
  const handleFilterChange = useOnChangeFilterHandler(name, onChange);

  const handleChange = useCallback(
    (newValue: ITaskOriginService[]) => {
      handleFilterChange([...value, ...newValue]);
    },
    [handleFilterChange, value],
  );

  const handleLanguageClick = useCallback(
    (taskOrigin: ITaskOriginService) => {
      handleFilterChange(value.filter(v => v.id !== taskOrigin?.id));
    },
    [handleFilterChange, value],
  );

  const filterAfterFetch = useCallback(
    (options: ITaskOriginService[]) => options.filter(option => !value?.find(v => v.id === option.id)),
    [value],
  );

  return (
    <Flex direction="column" innerMargin="sm">
      <CustomerCodelist
        name={name}
        onChange={handleChange}
        filterAfterFetch={filterAfterFetch}
        placeholder={t("session:TASKS.FILTERS.CUSTOMER_PLACEHOLDER")}
        components={{
          NoOptionsMessage: ({ children, ...restProps }) => {
            return (
              <ReactSelectComponents.NoOptionsMessage {...restProps}>
                <Text size={restProps.selectProps.size}>{t("COMPONENTS.FILTERS_CONTAINER.NO_MORE_OPTIONS")}</Text>
              </ReactSelectComponents.NoOptionsMessage>
            );
          },
        }}
        {...rest}
        value={[]}
        isMulti
        valueKey="id"
        labelKey="name"
      />
      {value && (
        <FloatedList>
          {value.map((taskOrigin: ITaskOriginService, index: number) => (
            <Flex key={index} align="center">
              <SkillRemove
                size="md"
                color="secondary"
                text={taskOrigin.name}
                onClick={() => handleLanguageClick(taskOrigin)}
              />
            </Flex>
          ))}
        </FloatedList>
      )}
    </Flex>
  );
};

export const CustomerFiler = React.memo(CustomerFilerComponent);
